// Stroke model shared by the drawer's phone and the board mirror.
// A stroke is { id, tool: "pen"|"eraser", color, size, pts: [[x, y], ...] }
// with x/y in 0..1 of a square canvas and size as a fraction of its width.
//
// Keep PALETTE in sync with tarbaa-lite-server sockets/draw.js and the app's
// src/utils/drawStrokes.js.
export const PALETTE = [
  "#111111",
  "#E53935",
  "#1E88E5",
  "#43A047",
  "#FDD835",
  "#8E24AA",
];
export const SIZES = [0.006, 0.014, 0.03];
export const ERASER_SCALE = 3;
const CANVAS_BG = "#FFFFFF";

/**
 * Mutable stroke list + change notification. Strokes arrive many times a
 * second, so this stays out of React state; canvases subscribe and repaint.
 */
export const createStrokeStore = () => {
  let strokes = [];
  const listeners = new Set();
  const changed = () => listeners.forEach((fn) => fn());

  return {
    get: () => strokes,
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    reset(next) {
      strokes = Array.isArray(next)
        ? next.map((s) => ({ ...s, pts: [...s.pts] }))
        : [];
      changed();
    },
    start({ id, tool, color, size, pt }) {
      strokes.push({ id, tool, color, size, pts: [pt] });
      changed();
    },
    pts({ id, pts }) {
      const stroke = strokes[strokes.length - 1];
      if (!stroke || stroke.id !== id) return;
      stroke.pts.push(...pts);
      changed();
    },
    undo() {
      if (!strokes.length) return;
      strokes.pop();
      changed();
    },
    clear() {
      strokes = [];
      changed();
    },
  };
};

/** Paint the whole stroke list onto a square canvas of `size` device px. */
export const paintStrokes = (ctx, strokes, size) => {
  ctx.fillStyle = CANVAS_BG;
  ctx.fillRect(0, 0, size, size);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  for (const stroke of strokes) {
    const { pts } = stroke;
    const color = stroke.tool === "eraser" ? CANVAS_BG : stroke.color;
    const width = Math.max(1, stroke.size * size);

    if (pts.length === 1) {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(pts[0][0] * size, pts[0][1] * size, width / 2, 0, Math.PI * 2);
      ctx.fill();
      continue;
    }

    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.beginPath();
    ctx.moveTo(pts[0][0] * size, pts[0][1] * size);
    // Curve through the midpoints so a fast finger doesn't draw corners.
    for (let i = 1; i < pts.length - 1; i++) {
      const midX = ((pts[i][0] + pts[i + 1][0]) / 2) * size;
      const midY = ((pts[i][1] + pts[i + 1][1]) / 2) * size;
      ctx.quadraticCurveTo(pts[i][0] * size, pts[i][1] * size, midX, midY);
    }
    const last = pts[pts.length - 1];
    ctx.lineTo(last[0] * size, last[1] * size);
    ctx.stroke();
  }
};
